import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { BookOpen, Heart, Award } from 'lucide-react';
import { articles, faqData } from '../data/mock';

const Infos = () => {
  const handleScrollTo = (targetId) => {
    if (!targetId) return;
    const el = document.getElementById(targetId);
    if (el) {
      el.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  // ordre des cibles pour les 3 cartes
  const targets = ['soins', 'repro', 'lof'];

  return (
    <div className="min-h-screen bg-black pt-20">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-b from-black to-black/95">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Informations <span className="text-[#cbb26a]">utiles</span>
          </h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto">
            Tout ce qu&apos;il faut savoir sur le Labrador et la reproduction
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4">
          {/* Articles */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">
              Articles et <span className="text-[#cbb26a]">conseils</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {articles.map((article, index) => {
                const targetId = targets[index] || 'soins';

                return (
                  <Card
                    key={article.id ?? index}
                    className="bg-white/5 border-[#cbb26a]/30 hover:bg-white/10 transition-all duration-300"
                  >
                    <CardHeader>
                      <div className="mb-4">
                        {index === 0 && <Heart size={32} className="text-[#cbb26a]" />}
                        {index === 1 && <BookOpen size={32} className="text-[#cbb26a]" />}
                        {index === 2 && <Award size={32} className="text-[#cbb26a]" />}
                      </div>
                      <CardTitle className="text-white">{article.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-white/70 mb-4">{article.excerpt}</p>
                      <button
                        type="button"
                        onClick={() => handleScrollTo(targetId)}
                        className="text-[#cbb26a] hover:text-[#d4c485] transition-colors duration-300 font-semibold"
                      >
                        Lire la suite →
                      </button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Soins du Labrador */}
          <div className="mb-16">
            <h2
              id="soins"
              className="scroll-mt-32 text-3xl font-bold text-white mb-8 text-center"
            >
              Les soins du <span className="text-[#cbb26a]">Labrador</span>
            </h2>
            <div className="max-w-4xl mx-auto">
              <Card className="bg-white/5 border-[#cbb26a]/30 mb-6">
                <CardHeader>
                  <CardTitle className="text-[#cbb26a]">Alimentation</CardTitle>
                </CardHeader>
                <CardContent className="text-white/80 space-y-3">
                  <p>
                    Le Labrador a besoin d&apos;une alimentation de qualité, adaptée à son âge, son
                    poids et son niveau d&apos;activité. Il est important de contrôler les portions
                    pour éviter le surpoids.
                  </p>
                  <ul className="space-y-2 pl-5">
                    <li className="flex items-start space-x-2">
                      <span className="text-[#cbb26a] mt-1">•</span>
                      <span>Croquettes premium pour grandes races</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#cbb26a] mt-1">•</span>
                      <span>2 repas par jour pour un adulte</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#cbb26a] mt-1">•</span>
                      <span>Eau fraîche toujours disponible</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#cbb26a] mt-1">•</span>
                      <span>Éviter les friandises excessives</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bg-white/5 border-[#cbb26a]/30 mb-6">
                <CardHeader>
                  <CardTitle className="text-[#cbb26a]">Santé</CardTitle>
                </CardHeader>
                <CardContent className="text-white/80 space-y-3">
                  <p>
                    Le Labrador est une race robuste, mais nécessite un suivi vétérinaire régulier et
                    des tests de dépistage pour les maladies héréditaires.
                  </p>
                  <ul className="space-y-2 pl-5">
                    <li className="flex items-start space-x-2">
                      <span className="text-[#cbb26a] mt-1">•</span>
                      <span>Vaccinations annuelles</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#cbb26a] mt-1">•</span>
                      <span>Test de dysplasie des hanches et coudes</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#cbb26a] mt-1">•</span>
                      <span>Examens ophtalmologiques</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#cbb26a] mt-1">•</span>
                      <span>Traitements antiparasitaires réguliers</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bg-white/5 border-[#cbb26a]/30">
                <CardHeader>
                  <CardTitle className="text-[#cbb26a]">Hygiène et entretien</CardTitle>
                </CardHeader>
                <CardContent className="text-white/80 space-y-3">
                  <p>
                    Le Labrador a un pelage facile à entretenir, mais nécessite des soins réguliers
                    pour rester en bonne santé.
                  </p>
                  <ul className="space-y-2 pl-5">
                    <li className="flex items-start space-x-2">
                      <span className="text-[#cbb26a] mt-1">•</span>
                      <span>Brossage 2-3 fois par semaine</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#cbb26a] mt-1">•</span>
                      <span>Bain tous les 2-3 mois</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#cbb26a] mt-1">•</span>
                      <span>Nettoyage régulier des oreilles</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#cbb26a] mt-1">•</span>
                      <span>Coupe des griffes si nécessaire</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* La reproduction */}
          <div className="mb-16">
            <h2
              id="repro"
              className="scroll-mt-32 text-3xl font-bold text-white mb-8 text-center"
            >
              La <span className="text-[#cbb26a]">reproduction</span> responsable
            </h2>
            <div className="max-w-4xl mx-auto">
              <Card className="bg-white/5 border-[#cbb26a]/30 mb-6">
                <CardHeader>
                  <CardTitle className="text-[#cbb26a]">Préparation de la femelle</CardTitle>
                </CardHeader>
                <CardContent className="text-white/80 space-y-3">
                  <p>
                    Avant toute saillie, la femelle doit être en excellente santé et répondre à
                    certains critères.
                  </p>
                  <ul className="space-y-2 pl-5">
                    <li className="flex items-start space-x-2">
                      <span className="text-[#cbb26a] mt-1">•</span>
                      <span>Âge minimum : 2 ans (pour la première portée)</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#cbb26a] mt-1">•</span>
                      <span>Poids idéal et bonne condition physique</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#cbb26a] mt-1">•</span>
                      <span>Tests de santé réalisés</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#cbb26a] mt-1">•</span>
                      <span>Chaleurs régulières et normales</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bg-white/5 border-[#cbb26a]/30 mb-6">
                <CardHeader>
                  <CardTitle className="text-[#cbb26a]">Le moment idéal</CardTitle>
                </CardHeader>
                <CardContent className="text-white/80 space-y-3">
                  <p>
                    La saillie doit être réalisée au moment optimal du cycle de la femelle.
                  </p>
                  <ul className="space-y-2 pl-5">
                    <li className="flex items-start space-x-2">
                      <span className="text-[#cbb26a] mt-1">•</span>
                      <span>Généralement entre le 11ème et 15ème jour des chaleurs</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#cbb26a] mt-1">•</span>
                      <span>Test de progéstérone recommandé</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#cbb26a] mt-1">•</span>
                      <span>2 saillies à 48h d&apos;intervalle pour maximiser les chances</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bg-white/5 border-[#cbb26a]/30">
                <CardHeader>
                  <CardTitle className="text-[#cbb26a]">Après la saillie</CardTitle>
                </CardHeader>
                <CardContent className="text-white/80 space-y-3">
                  <p>
                    Le suivi après la saillie est important pour confirmer la gestation.
                  </p>
                  <ul className="space-y-2 pl-5">
                    <li className="flex items-start space-x-2">
                      <span className="text-[#cbb26a] mt-1">•</span>
                      <span>Échographie de confirmation à partir de 3-4 semaines</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#cbb26a] mt-1">•</span>
                      <span>Durée de gestation : environ 63 jours</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#cbb26a] mt-1">•</span>
                      <span>Suivi vétérinaire régulier</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <span className="text-[#cbb26a] mt-1">•</span>
                      <span>Alimentation adaptée pour chienne gestante</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Le LOF */}
          <div className="mb-16">
            <h2
              id="lof"
              className="scroll-mt-32 text-3xl font-bold text-white mb-8 text-center"
            >
              Comprendre le <span className="text-[#cbb26a]">LOF</span>
            </h2>
            <div className="max-w-4xl mx-auto">
              <Card className="bg-white/5 border-[#cbb26a]/30">
                <CardHeader>
                  <CardTitle className="text-[#cbb26a]">Qu&apos;est-ce que le LOF ?</CardTitle>
                </CardHeader>
                <CardContent className="text-white/80 space-y-4">
                  <p>
                    Le LOF (Livre des Origines Français) est le registre généalogique officiel des
                    chiens de race en France, géré par la Société Centrale Canine (SCC).
                  </p>
                  <div>
                    <h4 className="text-white font-semibold mb-2">
                      Avantages de l&apos;inscription LOF :
                    </h4>
                    <ul className="space-y-2 pl-5">
                      <li className="flex items-start space-x-2">
                        <span className="text-[#cbb26a] mt-1">•</span>
                        <span>Garantie de pureté de la race</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-[#cbb26a] mt-1">•</span>
                        <span>Traçabilité complète du pedigree</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-[#cbb26a] mt-1">•</span>
                        <span>Participation aux expositions canines</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-[#cbb26a] mt-1">•</span>
                        <span>Possibilité de reproduction avec inscription des chiots</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-[#cbb26a] mt-1">•</span>
                        <span>Valeur ajoutée à la vente</span>
                      </li>
                    </ul>
                  </div>
                  <p className="text-[#cbb26a] font-semibold">
                    Important : Seuls les chiens LOF peuvent produire des chiots inscriptibles au LOF.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* FAQ */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">
              <span className="text-[#cbb26a]">FAQ</span> simplifiée
            </h2>
            <div className="max-w-3xl mx-auto">
              <Accordion type="single" collapsible className="space-y-4">
                {faqData.map((faq, index) => (
                  <AccordionItem
                    key={index}
                    value={`item-${index}`}
                    className="bg-white/5 border border-[#cbb26a]/30 rounded-lg px-6"
                  >
                    <AccordionTrigger className="text-white hover:text-[#cbb26a] transition-colors duration-300">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-white/80">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Infos;
